import React, { useState } from 'react';

import { Button, Card, Input, MenuItem, Select, CircularProgress } from '@mui/material';
import { getAccounts } from '../../sp-firebase/accountFB';
import { addNotification, copyInstruments, fixProducts, genInstruments } from './AdminUtils';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { useAccountStore } from '../../components/Stores/accountStore';
import { Timestamp } from 'firebase/firestore';
import { getAllFutureOrdersByAccount, saveExistingFutureOrder } from '../../sp-firebase/futureOrderFB';
import ButtonDesigned from '../../components/UI-Elements/Buttons/ButtonDesigned';
import { useNavigate } from 'react-router-dom';
import { createProduct } from '../../sp-firebase/productFB';
import { mockProducts } from '../../utils/mockData';
import { toast } from 'react-toastify';
import { DashboardNotification } from '@sp-types/notificationType';
import { getAllRoutes } from '../../sp-firebase/routesFB';

import { OverlayDisplayDiv } from '../../components/UI-Elements/PopoverOverlays/OverlayDisplayDiv';
import { getInstrumentKey, IInstrument } from '../../sp-types/instrumentType';
import { getInstrumentById } from '../../sp-firebase/instrumentFB';
import { makeMap } from '../../utils/routesUtils';
import { updateRoute } from '../../sp-firebase/routesFB';
import { IRoute } from '../../sp-types';
import { year1LetterInstrums } from '../BalanceFlow/balanceUtils';
import { FinancialPositionsPopup } from './admin-components/FinancialPositionsPopup';
import { triggerExposureCalc, triggerMonthTestALLACCOUNTS } from '../../hooks/useExposure';
import { OpenInNew, QuestionMarkOutlined } from '@mui/icons-material';
import { calcAccountExposeHistory } from '../../utils/exposureUtils';
import BasicTable from '../FinancialPositions/components/BasicTable';
import { customFormatter, getColDefs } from '../FinancialPositions/components/BasicTableUtils';
import {
  deleteExpHistoryForAccount,
  getExposureHistory,
  saveExposureHistory,
} from '../../sp-firebase/exposureHistoryFB';
import { HelpPopup } from './admin-components/HelpPopup';
import { deleteFutureExposure } from '../../sp-firebase/futureOrderExposureFB';

export const AdminChangeData = () => {
  const { account } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const navigate = useNavigate();

  const [selectedAccount, setSelectedAccount] = useState<string>(account?.id || 'iCJ07Boshorejsa8MtoA2');
  const [logs, setLogs] = React.useState<string[] | undefined>();
  const [allAccounts, setAllAccounts] = React.useState<string[]>([]);

  const getAllAccounts = async () => {
    setLoading(true);

    const accounts = await getAccounts();

    setAllAccounts(accounts?.map(({ id }) => id || '') || []);
    setSelectedAccount(accounts?.[0]?.id || '');

    setLoading(false);
  };

  React.useEffect(() => {
    getAllAccounts();
  }, [account]);

  const [notification, setNotification] = useState<DashboardNotification>({
    type: 'alert',
    date: Timestamp.now(),
    text: 'Material out of balance',
    descriptionText: 'the material HRCc1 is out of balance, please balance now!',
    id: 'ss',
    status: 'pending',
    entity: 'notification',
  });

  const [baseInstr, setBaseInstr] = useState<{ from: string; to: string }>({ from: 'HRC', to: 'BUSC' });
  const [instruments, setInstruments] = useState<{ base: string; fromDate: string; months: string }>({
    base: 'HRCV25',
    fromDate: '2026-01-01',
    months: '12',
  });

  const [loading, setLoading] = useState<boolean>(false);

  const createNotification = async () => {
    await addNotification(account.id || '', notification);
  };

  const copyInstr = async () => {
    setLoading(true);
    const resultCount = await copyInstruments(baseInstr.from, baseInstr.to);
    setLoading(false);
    toast.info(`created: ${resultCount} instructions`);
  };

  const generateInstruments = async () => {
    setLoading(true);
    const resultCount = await genInstruments(instruments.base, instruments.fromDate, instruments.months);
    setLoading(false);
    toast.info(`created: ${resultCount} ioio instructions`);
  };

  const fixRoutePrices = async () => {
    setLoading(true);

    setLogs(['getting relevant routes']);

    const allRoutes = await getAllRoutes();
    const now = new Date();

    const routeInstruments =
      allRoutes
        ?.filter(({ updatePriceFrom, updatePriceMonth }) => !!updatePriceFrom && !!updatePriceMonth)
        ?.map((route) => ({
          route,
          instrumentKey: calcInstrument(route.updatePriceFrom || '', route.updatePriceMonth || 0, now),
        })) || [];

    setLogs((log) => [...(log || []), `found ${routeInstruments.length} relevant routes`]);
    if (routeInstruments.length > 0) {
      const instruments = await Promise.all(
        routeInstruments.map(({ instrumentKey }) => getInstrumentById(instrumentKey)),
      );
      setLogs((log) => [...(log || []), `fetched ${instruments?.length || 0} instruments`]);
      if (instruments) {
        const instrumentsMap = makeMap<IInstrument>(instruments as any, 'id' as const);

        const routeUpdates = routeInstruments.map(({ route, instrumentKey }) => {
          route.prices.material.price = instrumentsMap[instrumentKey]?.lastPrice || route.prices.material.price;

          return updateRoute(route.id, route);
        });

        const routeResults = await Promise.all(routeUpdates);

        setLogs((log) => [
          ...(log || []),
          `updated ${routeResults?.length || 0} route prices`,
          ...routeResults.map(
            (r: IRoute | null) => `route: ${r?.name || '--'} price: ${r?.prices.material.price || '--'}`,
          ),
        ]);
      }
    }
    setLoading(false);
  };

  const calcInstrument = (baseKey: string, addMonth: number, now: Date): string => {
    const futureDate = new Date(
      Math.floor(now.getFullYear() + (now.getMonth() + addMonth) / 12),
      (now.getMonth() + addMonth) % 12,
      15,
    );

    const char1Year = year1LetterInstrums.includes(baseKey); //TODO: add other materials that has only 1 letter

    const futureKey = getInstrumentKey(futureDate, char1Year);

    return baseKey + futureKey;
  };

  const [finPosPopup, setFinPosPopup] = React.useState(false);
  const [recalcText, setRecalcText] = React.useState('---');
  const triggerExpCalc = async () => {
    setLoading(true);
    setRecalcText(
      'Recalculating exposure for: ' +
        account.id +
        ' materials: ' +
        account.materialsToAccount.map((a) => materialsMap[a.materialId]?.name || a.materialId).join(', '),
    );
    const result = await triggerExposureCalc({
      accountId: account.id || '',
      materialIds: account.materialsToAccount.map((a) => a.materialId),
    });
    console.log('Exposure calculation result:', result);
    setRecalcText(result);
    setLoading(false);
    setTimeout(() => {
      setRecalcText(`Recalc exposure of: ${account.id || ''}`);
    }, 5000);
  };

  const triggerMonthlyTest = async () => {
    setLoading(true);
    setRecalcText('Running monthly test for ALL ACCOUNTS');

    const result = await triggerMonthTestALLACCOUNTS();

    console.log('Monthly test result:', result);
    setRecalcText(`Month test result: ${result}`);
    setLoading(false);
  };

  const buildExposHistory = async () => {
    setLoading(true);

    try {
      const result = await calcAccountExposeHistory(selectedAccount, (text) =>
        setRecalcText((prev) => prev + ' ' + text),
      );

      console.log('Exposure history calculated:', result);
      const tableVals = Object.values(result) || [];

      setTableData(tableVals);
      setRecalcText((prev) => prev + ' saving history...');

      await Promise.all(tableVals.map((entry) => saveExposureHistory(entry)));

      setRecalcText((prev) => prev + ' saved ' + tableVals.length);

      // Show the number of history points created
      const historyCount = Object.keys(result).length;
      const materialIds = Array.from(new Set(Object.values(result).map((item) => item.materialId)));
      toast.success(
        `Generated ${historyCount} exposure history points across ${materialIds.length} materials for account: ${selectedAccount}`,
      );

      // Log a sample of the data for debugging
      const sampleKeys = Object.keys(result).slice(0, 5);
      sampleKeys.forEach((key) => {
        console.log(`${key}:`, result[key]);
      });
    } catch (error) {
      console.error('Error building exposure history:', error);
    }

    setLoading(false);
  };

  const showExistingExposures = async () => {
    setLoading(true);
    setRecalcText('fetching existing exposures');
    const exposures = await getExposureHistory(selectedAccount, new Date('2020-01-01'));

    setTableData(exposures);

    setRecalcText('found: ' + (exposures?.length || 0) + ' exposures');

    setLoading(false);
  };

  const buildFOrderExposure = async () => {
    setLoading(true);
    setRecalcText('fetching orders');
    const orders = await getAllFutureOrdersByAccount(selectedAccount, ['completed']);
    setRecalcText(`have ${orders?.length || 0} orders`);
    if (orders?.length) {
      for (let i = 0; i < orders.length; i++) {
        await saveExistingFutureOrder(orders[i]);
        setRecalcText(`have ${orders.length} orders.   updated ${i + 1} / ${orders.length}`);
      }
    }
    setLoading(false);
  };

  const deleteFOrderExp = async () => {
    setLoading(true);
    setRecalcText('deleting orders exposure');
    const orders = await deleteFutureExposure(selectedAccount);
    setRecalcText('deleted 1');

    console.log('deleted fut order exp', orders);
    const orde22rs = await deleteExpHistoryForAccount(selectedAccount);

    setLoading(false);
    setRecalcText('deleted 2');
  };

  const [tableData, setTableData] = React.useState<any[] | undefined>();
  const [showHelp, setShowHelp] = React.useState<boolean>(false);
  return (
    <div className="w-full h-full bg-white flex flex-col">
      <div className="tabPanel-0">
        <div className="flex flex-row gap-4 align-top h-20 m-0 p-2">
          <Button variant="contained" onClick={() => setFinPosPopup(true)} className="h-16 w-64">
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Add Financial Positions &nbsp;
            <OpenInNew />
          </Button>
          {finPosPopup && (
            <OverlayDisplayDiv setDisplayOverlay={() => setFinPosPopup(false)} hideClose>
              <FinancialPositionsPopup close={() => setFinPosPopup(false)} />
            </OverlayDisplayDiv>
          )}
          <Button variant="contained" onClick={triggerExpCalc} className="h-16 min-w-64">
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Recalc exposure of: {account.id || ''}
          </Button>
          <Button variant="contained" onClick={triggerMonthlyTest} className="h-16 min-w-64">
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            run monthly test ALL ACCOUNTS
          </Button>
          <div className="flex border text-sm" dangerouslySetInnerHTML={{ __html: recalcText }}></div>
          <Button variant="contained" onClick={() => setShowHelp(true)} className="h-8 bg-green-300">
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Help <QuestionMarkOutlined />
          </Button>
          {showHelp && (
            <OverlayDisplayDiv setDisplayOverlay={() => setShowHelp(false)} hideClose>
              <HelpPopup close={() => setShowHelp(false)} />
            </OverlayDisplayDiv>
          )}
        </div>
        <Card className="flex flex-row gap-4 align-top h-32 m-4 p-4">
          <Select
            value={selectedAccount}
            onChange={({ target }) => setSelectedAccount(target.value)}
            placeholder="enter accountId"
          >
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}

            {allAccounts.map((accountId) => (
              <MenuItem key={accountId} value={accountId}>
                {accountId}
              </MenuItem>
            ))}
          </Select>
          <Button onClick={buildFOrderExposure} className="w-64" variant="contained">
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            generate future order exposure for: {selectedAccount}
          </Button>
          <Button onClick={buildExposHistory} className="w-64" variant="contained">
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            build exposure history for: {selectedAccount}
          </Button>{' '}
          <Button onClick={deleteFOrderExp} className="w-64" variant="contained">
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Delete for: {selectedAccount}
          </Button>
          <Button
            onClick={() => {
              setTableData(undefined);
              setRecalcText('');
            }}
            className="w-64"
            variant="contained"
          >
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Clear logs
          </Button>
          <Button onClick={showExistingExposures} className="w-64" variant="contained">
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Show existing exposures
          </Button>
        </Card>
        {tableData && (
          <BasicTable
            allRows={tableData}
            columns={getColDefs(tableData[0], customFormatter)}
            options={{ cellClass: 'max-h-24 overflow-scroll' }}
          ></BasicTable>
        )}
        <Card className="flex flex-row gap-4 align-top h-32 m-4 p-4">
          <Select
            placeholder="notification type"
            value={notification.type}
            onChange={({ target }) => setNotification((notifi) => ({ ...notifi, type: target.value as any }))}
          >
            {['alert', 'info', 'action'].map((item, index) => (
              <MenuItem id={item} value={item} key={index}>
                {item}
              </MenuItem>
            ))}
          </Select>
          <Input
            onChange={({ target }) => setNotification((notifi) => ({ ...notifi, text: target.value }))}
            placeholder="text"
            value={notification.text}
          ></Input>
          <Input
            onChange={({ target }) => setNotification((notifi) => ({ ...notifi, descriptionText: target.value }))}
            placeholder="description"
            value={notification.descriptionText}
          ></Input>
          {/* <Select placeholder="add action" value={notification.actionText}>
            {[{ actionText: '' }, { actionText: 'Balance now', actionUrl: 'balance' }].map(
              ({ actionText, actionUrl }, index) => (
                <MenuItem
                  id={actionText}
                  value={actionText}
                  key={index}
                  onClick={() => setNotification((notifi) => ({ ...notifi, actionText, actionUrl }))}
                >
                  {actionText}
                </MenuItem>
              ),
            )}
          </Select> */}
          {/* <Select placeholder="materialId" value={notification.materialId}>
            {Object.keys(materialsMap).map((materialId, index) => (
              <MenuItem
                id={materialId}
                value={materialId}
                key={index}
                onClick={() => setNotification((notifi) => ({ ...notifi, materialId: [materialId] }))}
              >
                {materialId}
              </MenuItem>
            ))}
          </Select> */}
          <Button onClick={createNotification} className="w-40 relative flex" variant="contained" disabled={loading}>
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Add notification
          </Button>
          {/* <Button onClick={fixMaterialNames} className="w-40 relative flex" variant="contained" disabled={loading}>
              {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
              Fix material names
            </Button>*/}
          <Button
            onClick={() => fixProducts(account?.id || '')}
            className="w-40 relative flex"
            variant="contained"
            disabled={loading}
          >
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Fix products
          </Button>
          <Input
            onChange={({ target }) => setBaseInstr(({ to }) => ({ to, from: target.value }))}
            placeholder="from instrument"
            value={baseInstr.from}
          ></Input>
          -&gt;
          <Input
            onChange={({ target }) => setBaseInstr(({ from }) => ({ from, to: target.value }))}
            placeholder="to instrument"
            value={baseInstr.to}
          ></Input>
          <Button onClick={copyInstr} className="w-40 relative flex" variant="contained" disabled={loading}>
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Copy instruments
          </Button>
        </Card>
        <Card className="flex max-h-20 p-3 gap-2">
          <Input
            onChange={({ target }) => setInstruments({ ...instruments, base: target.value })}
            placeholder="from instrument"
            value={instruments.base}
          ></Input>
          <Input
            type="date"
            value={instruments.fromDate}
            onChange={({ target }) => setInstruments({ ...instruments, fromDate: target.value })}
          ></Input>
          <Input
            type="number"
            onChange={({ target }) => setInstruments({ ...instruments, months: target.value })}
            value={instruments.months}
          ></Input>

          <Button onClick={generateInstruments} className="max-h-16" variant="contained" disabled={loading}>
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Copy instruments
          </Button>

          <Button onClick={fixRoutePrices} className="max-h-16" variant="contained" disabled={loading}>
            {loading && <CircularProgress size={24} className="absolute align-middle justify-center" />}
            Update route prices
          </Button>
          {logs && (
            <OverlayDisplayDiv setDisplayOverlay={() => setLogs(undefined)}>
              <div className="flex flex-col gap-3 bg-white p-3 rounded text-blue-600">
                {logs?.map((l, i) => (
                  <p key={i}>{l}</p>
                ))}
              </div>
            </OverlayDisplayDiv>
          )}
        </Card>
        <ButtonDesigned
          className="max-w-[20rem]"
          onButtonClick={() => {
            navigate('/change-route-details');
          }}
        >
          Edit Route Details - Navigate
        </ButtonDesigned>
        <ButtonDesigned
          className="max-w-[20rem] "
          onButtonClick={async () => {
            await mockProducts.forEach(async (item) => account.id && (await createProduct(account.id, item)));
          }}
        >
          Add Mock Products to current Account
        </ButtonDesigned>
      </div>
    </div>
  );
};
